import LiveFeedback from './LiveFeedback';
import { ReactNode, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import api from "../../../../Component/axios_Interceptor/api";
import url, { SERVER_ADDRESS } from "../../../../Component/server";
import { fontStyles } from "../../../../Component/style/admin_font";
import { mediaUrl } from "../../../../Component/tool/mediaUrl";
import LiveDetailDialog from "./LiveDetailDialog";
import PaymentPolicyFields, { PaymentPolicy, paymentPolicyError, readPaymentPolicy } from './PaymentPolicyFields';
import AdminLiveVisibilityBadge from './AdminLiveVisibilityBadge';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';

const capacities = [...Array.from({ length: 20 }, (_, i) => (i + 1) * 50), 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000];

type Props = {
  setLive_cancle_check: React.Dispatch<React.SetStateAction<boolean>>;
  operations?: ReactNode;
  onPaymentPolicyChange?: (mode: 'pg' | 'combined' | 'bank') => void;
  canEditPayment?: boolean;
  canEditTitle?: boolean;
  canEditCapacity?: boolean;
  canEditImage?: boolean;
  canCancel?: boolean;
  onCancelled?: () => void;
  disabled?: boolean;
  refreshVersion?: number;
};

const LiveInfo = ({ setLive_cancle_check, operations, onPaymentPolicyChange, canEditPayment = true, canEditTitle = true, canEditCapacity = true, canEditImage = false, canCancel = false, onCancelled, disabled = false, refreshVersion = 0 }: Props) => {
  const { seq } = useParams();
  const [createdAt, setCreatedAt] = useState("");
  const [title, setTitle] = useState("");
  const [image, setImage] = useState("");
  const [imageOpen, setImageOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDraftUrl, setImageDraftUrl] = useState("");
  const [imageSaving, setImageSaving] = useState(false);
  const [imageError, setImageError] = useState("");
  const [imageMessage, setImageMessage] = useState("");
  const [limit, setLimit] = useState(0);
  const [paidLimit, setPaidLimit] = useState(0);
  const [selectedLimit, setSelectedLimit] = useState(0);
  const [status, setStatus] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const cancelPending = useRef(false);
  const [secretYn, setSecretYn] = useState<string | null>(null);
  const [start, setStart] = useState("");
  const [finish, setFinish] = useState("");
  const [capacityOpen, setCapacityOpen] = useState(false);
  const [titleOpen, setTitleOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleError, setTitleError] = useState("");
  const [titleMessage, setTitleMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [paymentPolicy, setPaymentPolicy] = useState<PaymentPolicy>(() => readPaymentPolicy());
  const [paymentDraft, setPaymentDraft] = useState<PaymentPolicy>(() => readPaymentPolicy());
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [policyReady, setPolicyReady] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const policySavingRef = useRef(false);
  const infoRequest = useRef(0);
  const [policyError, setPolicyError] = useState('');
  const [policyMessage, setPolicyMessage] = useState('');
  const actionsDisabled = disabled || !['예약', '리허설', '방송중'].includes(status);
  const canChangePaymentMode = ['예약', '리허설'].includes(status);
  const canChangeBroadcastInfo = !actionsDisabled && (status === '예약' || status === '방송중');

  const cancelLive = async () => {
    if (!canCancel || disabled || status !== '예약' || cancelPending.current || !window.confirm('정말로 취소하시겠습니까?')) return;
    cancelPending.current = true;
    setCancelling(true);
    setCancelError('');
    try {
      const { data } = await api.post(url.Communication_url.live_cancel, { seq: Number(seq) });
      if (data !== '성공') throw new Error('라이브 취소 결과를 확인하지 못했습니다. 다시 시도해주세요.');
      setStatus('취소');
      onCancelled?.();
    } catch (cause: any) {
      setCancelError(cause?.response?.data?.message || cause?.message || '라이브 취소에 실패했습니다. 다시 시도해주세요.');
    } finally {
      cancelPending.current = false;
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (!actionsDisabled) return;
    setImageOpen(false);
    setImageFile(null);
    setImageDraftUrl('');
    setTitleOpen(false);
    setCapacityOpen(false);
    setPaymentOpen(false);
  }, [actionsDisabled]);

  const get_live_info = async () => {
    const request = ++infoRequest.current;
    setPolicyReady(false);
    try {
      const { data } = await api.get(url.Communication_url.get_live_info, { params: { seq } });
      if (request !== infoRequest.current) return false;
      const policy = readPaymentPolicy(data, data.live_finish);
      setPaymentPolicy(policy);
      setPolicyError('');
      onPaymentPolicyChange?.(policy.payment_mode);
      setPolicyReady(true);
      setCreatedAt(data.crt_dt || "");
      setTitle(data.live_title || "");
      setImage(mediaUrl(data.live_img));
      setLimit(Number(data.live_limit) || 0);
      setPaidLimit(Number(data.paid_live_limit) || Number(data.live_limit) || 0);
      setStatus(data.live_status || "");
      setSecretYn(data.secret_yn ?? null);
      setStart(data.live_start || "");
      setFinish(data.live_finish || "");
      setLive_cancle_check(data.cancle_check === "y");
      return true;
    } catch {
      if (request === infoRequest.current) setPolicyError('라이브 정보를 불러오지 못했습니다. 다시 불러오기를 눌러주세요.');
      return false;
    }
  };
  useEffect(() => {
    setSecretYn(null);
    void get_live_info();
    return () => { infoRequest.current += 1; };
  }, [seq, setLive_cancle_check, refreshVersion]);

  useEffect(() => () => {
    if (imageDraftUrl) URL.revokeObjectURL(imageDraftUrl);
  }, [imageDraftUrl]);

  const closeImage = () => {
    if (imageSaving) return;
    setImageOpen(false);
    setImageFile(null);
    setImageDraftUrl("");
    setImageError("");
  };

  const selectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageError("");
    if (!['image/jpeg', 'image/png'].includes(file.type) || !file.size || file.size > 10 * 1024 * 1024) {
      setImageFile(null);
      setImageDraftUrl("");
      setImageError('JPG 또는 PNG 이미지를 10MB 이하로 선택해주세요.');
      event.target.value = '';
      return;
    }
    setImageFile(file);
    setImageDraftUrl(URL.createObjectURL(file));
  };

  const saveImage = async () => {
    if (actionsDisabled || !seq || !imageFile || imageSaving || !canEditImage) return;
    setImageSaving(true);
    setImageError("");
    setImageMessage("");
    try {
      const form = new FormData();
      form.append('file', imageFile);
      const { data } = await api.post(`${url.Communication_url.replace_live_image}?live_seq=${encodeURIComponent(seq)}`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (typeof data?.live_img !== 'string' || !data.live_img.trim()) throw new Error('저장된 이미지 경로가 없습니다.');
      setImage(mediaUrl(data.live_img));
      setImageMessage('대표 이미지가 변경되었습니다.');
      setImageOpen(false);
      setImageFile(null);
      setImageDraftUrl('');
    } catch (failure: any) {
      setImageError(failure?.response?.data?.message || '대표 이미지를 저장하지 못했습니다. 다시 시도해주세요.');
    } finally {
      setImageSaving(false);
    }
  };

  const savePaymentPolicy = async () => {
    if (actionsDisabled || !canEditPayment || !seq || !policyReady || policySavingRef.current) return;
    const validation = paymentPolicyError(paymentDraft);
    if (validation) { setPolicyError(validation); return; }
    policySavingRef.current = true;
    setPolicySaving(true); setPolicyError(''); setPolicyMessage('');
    const request = infoRequest.current;
    try {
      await api.post(`${SERVER_ADDRESS}live/payment-policy`, { seq: Number(seq), ...paymentDraft }, { headers: { 'Content-Type': 'application/json' } });
      if (request !== infoRequest.current) return;
      if (!await get_live_info()) {
        setPaymentOpen(false);
        return;
      }
      setPaymentOpen(false);
      setPolicyMessage('라이브 결제 설정이 저장되었습니다.');
    } catch (error: any) {
      if (request === infoRequest.current) {
        const message = error?.response?.data?.message;
        setPolicyError(typeof message === 'string' ? message : '결제 설정을 저장하지 못했습니다. 입력 내용을 확인하고 다시 시도해주세요.');
      }
    } finally { policySavingRef.current = false; setPolicySaving(false); }
  };

  const openCapacity = () => {
    if (!canChangeBroadcastInfo || !canEditCapacity) return;
    setSelectedLimit(limit);
    setError("");
    setCapacityOpen(true);
  };
  const openTitle = () => {
    if (!canChangeBroadcastInfo || !canEditTitle) return;
    setEditedTitle(title);
    setTitleError("");
    setTitleOpen(true);
  };
  const openPayment = () => {
    if (actionsDisabled || !canEditPayment || !policyReady) return;
    setPaymentDraft({ ...paymentPolicy });
    setPolicyError('');
    setPolicyMessage('');
    setPaymentOpen(true);
  };
  const saveTitle = async () => {
    if (!canChangeBroadcastInfo || titleSaving || !canEditTitle || !seq) return;
    const nextTitle = editedTitle.trim();
    if (!nextTitle || nextTitle.length > 50) { setTitleError("라이브 제목은 1~50자로 입력해주세요."); return; }
    setTitleSaving(true); setTitleError(""); setTitleMessage("");
    try {
      const { data } = await api.patch(`${SERVER_ADDRESS}live/title`, { seq: Number(seq), live_title: nextTitle });
      if (data?.live_title !== nextTitle) throw new Error("저장 결과를 확인하지 못했습니다.");
      setTitle(nextTitle);
      setTitleOpen(false);
      setTitleMessage("라이브 제목이 변경되었습니다.");
    } catch (failure: any) {
      setTitleError(failure?.response?.data?.message || "라이브 제목을 변경하지 못했습니다. 다시 시도해주세요.");
    } finally { setTitleSaving(false); }
  };
  const hours = (Date.parse(finish.replace(" ", "T")) - Date.parse(start.replace(" ", "T"))) / 3_600_000;
  const additionalCoins = Number.isInteger(hours) && hours > 0
    ? Math.max(0, selectedLimit - Math.max(paidLimit, limit)) * hours * 114 / 25 : 0;

  const saveCapacity = async () => {
    if (!canChangeBroadcastInfo || !canEditCapacity || saving || !seq || selectedLimit === limit) return;
    setSaving(true); setError(""); setMessage("");
    try {
      const { data } = await api.patch(`${SERVER_ADDRESS}live/capacity`, { seq: Number(seq), live_limit: selectedLimit });
      setLimit(Number(data.live_limit));
      setPaidLimit(Number(data.paid_live_limit) || Math.max(paidLimit, limit, selectedLimit));
      setCapacityOpen(false);
      const chargedCoins = Number(data.charged_coins) || 0;
      setMessage(`수용 인원을 ${Number(data.live_limit).toLocaleString()}명으로 변경했습니다.${chargedCoins ? ` ${chargedCoins.toLocaleString()}C가 사용되었습니다.` : ''}`);
    } catch (failure: any) {
      setError(failure?.response?.data?.message || "인원수를 변경하지 못했습니다.");
    } finally { setSaving(false); }
  };

  const formatTime = () => {
    if (!start || !finish) return "";
    const dateText = (value: string) => {
      const date = value.slice(0, 10);
      const day = ["일", "월", "화", "수", "목", "금", "토"][new Date(`${date}T00:00:00`).getDay()];
      return `${date.replaceAll("-", "/")}(${day})`;
    };
    const hourText = (value: string) => {
      const hour = Number(value.slice(11, 13));
      return `${hour < 12 ? "오전" : "오후"} ${hour % 12 || 12}:00`;
    };
    const end = start.slice(0, 10) === finish.slice(0, 10) ? "" : `${dateText(finish)} `;
    return `${dateText(start)} ${hourText(start)} ~ ${end}${hourText(finish)}`;
  };
  const unavailableReason = !status ? '방송 정보를 불러오는 중입니다.'
    : status === '종료' || status === '취소'
      ? '종료·취소된 방송은 제목과 인원 수를 변경할 수 없습니다.'
      : '제목과 인원 수는 예약 또는 방송 중인 라이브에서 변경할 수 있습니다.';

  return <>
    <InformationGrid>
      <SummaryCard aria-label="라이브 기본 정보">
        <SummaryContent>
          <LiveImage>{image ? <img src={image} alt="라이브 대표 이미지" /> : <span>이미지 없음</span>}
            {canEditImage && <EditImageButton type="button" aria-label="라이브 대표 이미지 수정"
              disabled={actionsDisabled}
              onClick={() => { setImageMessage(''); setImageOpen(true); }}>이미지 변경</EditImageButton>}
          </LiveImage>
          <SummaryDetails>
            <TitleRow><LiveTitle>{title || "라이브 정보를 불러오는 중입니다."}</LiveTitle>
              <AdminLiveVisibilityBadge secretYn={secretYn} />
              {canEditTitle && <EditTitleButton type="button" aria-label="라이브 제목 수정" disabled={!canChangeBroadcastInfo}
                title={!canChangeBroadcastInfo ? unavailableReason : undefined} onClick={openTitle}>수정하기</EditTitleButton>}
            </TitleRow>
            {createdAt && <DetailRow><span>등록일</span><RegisteredAt dateTime={createdAt.slice(0, 10)}>{createdAt.slice(0, 10).replaceAll("-", "/")}</RegisteredAt></DetailRow>}
            {start && finish && <DetailRow><span>일정</span><span>{formatTime()}</span></DetailRow>}
            <DetailRow><span>인원 수</span>
              {canEditCapacity
                ? <CapacityButton type="button" aria-label="인원수 변경" disabled={!canChangeBroadcastInfo}
                  title={!canChangeBroadcastInfo ? unavailableReason : undefined} onClick={openCapacity}>{limit.toLocaleString()}명 <span aria-hidden="true">›</span></CapacityButton>
                : <span>{limit.toLocaleString()}명</span>}
            </DetailRow>
            <DetailRow><span>결제 수단</span><PaymentSummary>
              <span>{!policyReady ? '불러오는 중…' : paymentPolicy.payment_mode === 'combined' ? 'PG 결제 + 무통장 입금' : paymentPolicy.payment_mode === 'bank' ? '무통장 입금만' : 'PG 결제만'}</span>
              {canEditPayment && <EditTitleButton type="button" aria-label="고객 결제 수단 수정" disabled={actionsDisabled || !policyReady} onClick={openPayment}>수정하기</EditTitleButton>}
            </PaymentSummary></DetailRow>
          </SummaryDetails>
        </SummaryContent>
        {message && <Feedback as={LiveFeedback} role="status">{message}</Feedback>}
        {imageMessage && <Feedback as={LiveFeedback} role="status">{imageMessage}</Feedback>}
        {titleMessage && <Feedback as={LiveFeedback} role="status">{titleMessage}</Feedback>}
        {!capacityOpen && error && <Feedback as={LiveFeedback} role="alert">{error}</Feedback>}
        {policyMessage && <Feedback as={LiveFeedback} role="status">{policyMessage}</Feedback>}
        {!paymentOpen && policyError && <Feedback as={LiveFeedback} role="alert">{policyError}</Feedback>}
        {!policyReady && policyError && <RetryButton type="button" onClick={get_live_info}>라이브 정보 다시 불러오기</RetryButton>}
        {cancelError && <Feedback as={LiveFeedback} role="alert">{cancelError}</Feedback>}
      </SummaryCard>
      {(operations || (canCancel && status === '예약')) && <OperationsArea>{operations}
        {canCancel && status === '예약' && <DangerActions><AdminActionButton type="button" disabled={disabled || cancelling} onClick={() => void cancelLive()}>{cancelling ? '취소 중…' : '라이브 취소'}</AdminActionButton></DangerActions>}
      </OperationsArea>}
    </InformationGrid>
    <LiveDetailDialog scrollBody={false} open={imageOpen && !actionsDisabled} onClose={closeImage} title="라이브 대표 이미지 수정" size="compact">
      <ImageFields data-admin-modal-content=""><div data-admin-modal-body="" data-admin-modal-fields="">
        <ImagePreview>{imageDraftUrl || image ? <img src={imageDraftUrl || image} alt="변경할 대표 이미지 미리보기" /> : '이미지 없음'}</ImagePreview>
        <ImagePicker $disabled={imageSaving}>
          <ImagePickerAction>
            <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 16V4m0 0-4 4m4-4 4 4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
            </svg>
            새 이미지 선택
          </ImagePickerAction>
          <ImagePickerFile title={imageFile?.name || undefined}>{imageFile?.name || 'JPG·PNG, 최대 10MB'}</ImagePickerFile>
          <input type="file" accept="image/jpeg,image/png" aria-label="새 대표 이미지"
            disabled={imageSaving} onChange={selectImage} />
        </ImagePicker>
        {imageError && <Feedback as={LiveFeedback} role="alert">{imageError}</Feedback>}
        </div><DialogActions data-admin-modal-footer=""><button type="button" disabled={imageSaving} onClick={closeImage}>취소</button>
          <button type="button" disabled={!imageFile || imageSaving} onClick={saveImage}>{imageSaving ? '저장 중…' : '이미지 저장'}</button>
        </DialogActions>
      </ImageFields>
    </LiveDetailDialog>
    <LiveDetailDialog footer={<PaymentActions>
        <button type="button" onClick={() => setPaymentOpen(false)} disabled={policySaving}>취소</button>
        <button type="button" disabled={!canEditPayment || !policyReady || policySaving} onClick={savePaymentPolicy}>{policySaving ? '저장 중…' : '결제 설정 저장'}</button>
      </PaymentActions>} open={paymentOpen && !actionsDisabled} onClose={() => !policySaving && setPaymentOpen(false)} title="고객 결제 수단 수정"
      description={canChangePaymentMode ? '이 라이브에서 고객이 사용할 결제 수단과 입금 계좌를 설정합니다.' : '방송 중에는 결제 수단 종류를 바꿀 수 없으며 입금 계좌 정보만 수정할 수 있습니다.'} size="medium">
      <PaymentPolicyFields buttonChoices value={paymentDraft} liveFinish={finish} onChange={value => { setPaymentDraft(value); setPolicyError(''); }}
        disabled={!canEditPayment || !policyReady || policySaving} modeDisabled={!canChangePaymentMode} />
      {policyError && <Feedback as={LiveFeedback} role="alert">{policyError}</Feedback>}
      
    </LiveDetailDialog>
    <LiveDetailDialog scrollBody={false} open={titleOpen && !actionsDisabled} onClose={() => !titleSaving && setTitleOpen(false)} title="라이브 제목 수정" size="compact">
      <TitleFields data-admin-modal-content="" onSubmit={event => { event.preventDefault(); void saveTitle(); }}><div data-admin-modal-body="" data-admin-modal-fields="">
        <label htmlFor="edit-live-title">라이브 제목</label>
        <input id="edit-live-title" value={editedTitle} maxLength={50} onChange={event => { setEditedTitle(event.target.value); setTitleError(""); }} />
        <small>{editedTitle.length}/50자</small>
        {titleError && <LiveFeedback role="alert">{titleError}</LiveFeedback>}
        </div><DialogActions data-admin-modal-footer=""><button type="button" onClick={() => setTitleOpen(false)} disabled={titleSaving}>취소</button>
          <button type="submit" disabled={titleSaving || !editedTitle.trim() || editedTitle.trim() === title}>
            {titleSaving ? "저장 중…" : "변경 완료"}
          </button></DialogActions>
      </TitleFields>
    </LiveDetailDialog>
    <LiveDetailDialog scrollBody={false} open={capacityOpen && !actionsDisabled} onClose={() => setCapacityOpen(false)} title="인원 수 변경" size="compact">
      <CapacityFields data-admin-modal-content=""><div data-admin-modal-body="" data-admin-modal-fields="">
        <label>최대 참여 인원
          <select value={selectedLimit} disabled={saving} onChange={event => setSelectedLimit(Number(event.target.value))}>
            {capacities.map(value => <option key={value} value={value}>{value.toLocaleString()}명</option>)}
          </select>
        </label>
        <CapacityNote>라이브 진행 중 인원 수를 변경해도 기존 참여 인원은 유지됩니다.<br />현재 참여 중인 인원보다 적게 변경할 수 없습니다.
          <br />인원을 줄여도 이미 사용한 코인은 반환되지 않습니다.</CapacityNote>
        {additionalCoins > 0 && <p>추가 사용 코인: {additionalCoins.toLocaleString()}C</p>}
        {error && <LiveFeedback role="alert">{error}</LiveFeedback>}
        </div><DialogActions data-admin-modal-footer=""><button type="button" onClick={() => setCapacityOpen(false)} disabled={saving}>취소</button>
          <button type="button" disabled={saving || selectedLimit === limit || !Number.isFinite(additionalCoins)} onClick={saveCapacity}>
            {saving ? "변경 중…" : "변경 완료"}
          </button></DialogActions>
      </CapacityFields>
    </LiveDetailDialog>
  </>;
};

export default LiveInfo;

const InformationGrid = styled.div`
  display: flex; flex-direction: column; gap: var(--space-5);
`;
const SummaryCard = styled.section`
  min-width: 0; color: var(--color-text-primary);
`;
const DangerActions = styled.div`
  display: flex; flex: 0 0 auto;
  button { color: var(--color-text-danger); }
`;
const PaymentActions = styled.div`
  display: flex; justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--admin-theme-border, #e8e4ed);
  button { min-height: 44px; padding: 10px 18px; border: 1px solid var(--admin-theme-border, #e0dce8); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-muted, #625d6a); font-size: 14px; cursor: pointer; }
  button:last-child { min-width: 150px; border-color: var(--admin-accent-deep); background: var(--admin-accent-deep); color: #fff; font-weight: 600; }
  button:disabled { opacity: .55; cursor: default; }
  button:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  @media (max-width: 520px) { button { flex: 1; } }
`;
const RetryButton = styled.button`
  min-height: 36px; margin-top: 10px; padding: 0 12px; border: 1px solid var(--admin-accent); border-radius: 6px;
  background: var(--admin-theme-surface, #fff); color: var(--admin-theme-link, #5730c4); cursor: pointer;
`;
const RegisteredAt = styled.time`
  color: var(--color-text-secondary); font-size: var(--text-body); font-variant-numeric: tabular-nums;
`;
const SummaryContent = styled.div`
  display: grid; grid-template-columns: calc(var(--space-12) * 2.5) minmax(0, 1fr); gap: var(--space-4); align-items: start;
  @media (max-width: 520px) { grid-template-columns: minmax(0, 1fr); gap: var(--space-4); }
`;
const SummaryDetails = styled.div`min-width: 0;`;
const TitleRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); margin-bottom: var(--space-2);
`;
const LiveTitle = styled.div`min-width: 0; overflow-wrap: anywhere; font-size: var(--text-h2); font-weight: var(--font-weight-semibold); line-height: var(--leading-h2);`;
const DetailRow = styled.div`
  display: grid; grid-template-columns: 5rem minmax(0, 1fr); align-items: center; gap: var(--space-3); min-height: var(--control-sm);
  color: var(--color-text-primary); font-size: var(--text-body); line-height: var(--leading-body);
  > span:first-child { color: var(--color-text-secondary); }
`;
const PaymentSummary = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-3); min-width: 0;
`;
const LiveImage = styled.div`
  position: relative; overflow: hidden; width: 100%; aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface);
  color: var(--color-text-secondary); font-size: var(--text-caption);
  img { width: 100%; height: 100%; object-fit: cover; }
  @media(max-width:520px) { width: calc(var(--space-12) * 2.5); }
`;
const EditImageButton = styled.button`
  position: absolute; right: 0; bottom: 0; left: 0; min-height: 28px; border: 0;
  background: rgba(35, 30, 45, .82); color: #fff; font-size: 11px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { background: rgba(35, 30, 45, .96); }
  &:disabled { opacity: .5; cursor: default; }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: -3px; }
`;
const ImageFields = styled.div`
  display: flex; flex-direction: column; align-items: stretch; gap: 16px;
`;
const ImagePicker = styled.label<{ $disabled: boolean }>`
  position: relative; display: flex; align-items: center; gap: 10px; min-height: 62px;
  padding: 8px; border: 1px solid var(--admin-theme-border, #ded6f3); border-radius: 12px;
  background: var(--admin-theme-raised, #faf9ff); cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  transition: border-color .15s, background-color .15s;
  &:hover { border-color: ${props => props.$disabled ? 'var(--admin-theme-border, #ded6f3)' : '#805de1'}; background: var(--admin-theme-accent-soft, #f7f3ff); }
  &:focus-within { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  input { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
  @media (max-width: 480px) { align-items: stretch; flex-direction: column; }
`;
const ImagePickerAction = styled.span`
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  min-height: 42px; padding: 0 14px; border-radius: 8px;
  background: #5b32cb; color: #fff; font-size: 13px; font-weight: 700;
`;
const ImagePickerFile = styled.span`
  min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: var(--admin-theme-muted, #756e83); font-size: 12px;
`;
const ImagePreview = styled.div`
  width: 160px; height: 160px; overflow: hidden; align-self: center; display: flex;
  align-items: center; justify-content: center; border: 1px solid var(--admin-theme-border, #e9e6ef); border-radius: 10px;
  background: var(--admin-theme-raised, #f7f6f9); color: var(--admin-theme-muted, #8e8995); font-size: 13px;
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const EditTitleButton = styled.button`
  flex: 0 0 auto; min-height: var(--control-sm); padding: 0 var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; font-size: var(--text-caption); cursor: pointer;
  &:hover:not(:disabled) { border-color: var(--color-brand); color: var(--color-text-link); }
  &:disabled { color: var(--color-text-disabled); background: var(--color-bg-muted); cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
const OperationsArea = styled.div`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; min-width: 0; gap: var(--space-3);
  padding-top: var(--space-4); border-top: 1px solid var(--color-border-default);
  > section { flex: 1; margin: 0; padding: 0; border: 0; }
  @media(max-width:520px) { > section { min-width:0; } }
`;
const CapacityButton = styled.button`
  justify-self: start; padding: 0; border: 0; color: var(--admin-theme-link, var(--admin-accent-deep)); background: transparent;
  font-size: 13px; font-weight: 600; line-height: inherit; cursor: pointer;
  span { margin-left: 4px; font-size: 18px; line-height: 12px; vertical-align: -1px; }
  &:disabled { color: var(--admin-theme-muted, #8e8995); cursor: default; }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;
const Feedback = styled.p`margin: 10px 0 0; color: var(--admin-theme-success, #246b36); font-size: 13px; &[role="alert"] { color: var(--admin-theme-danger, #bd0030); }`;
const CapacityFields = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  label { display: flex; flex-direction: column; gap: 10px; color: var(--admin-theme-text, #302a38); font-size: 14px; font-weight: 600; }
  select { box-sizing: border-box; width: 100%; min-height: 48px; padding: 0 14px; border: 1px solid var(--admin-theme-border, #d5d0df); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #302a38); color-scheme: var(--admin-color-scheme, light); font-size: 16px; font-weight: 400; }
  option { background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #302a38); }
  p { margin: 0; text-align: right; color: var(--admin-theme-text, #555); font-size: 13px; }
  p[role="alert"] { color: var(--admin-theme-danger, #bd0030); }
`;
const CapacityNote = styled.div`
  padding: 14px 16px; border-radius: 8px; background: var(--admin-theme-accent-soft, #f5f1ff); color: var(--admin-theme-muted, #625d6a); font-size: 12px; line-height: 1.6;
`;
const TitleFields = styled.form`
  display: flex; flex-direction: column; gap: 12px;
  label { color: var(--admin-theme-text, #302a38); font-size: 14px; font-weight: 600; }
  input { box-sizing: border-box; width: 100%; min-height: 48px; padding: 10px 12px; border: 1px solid var(--admin-theme-border, #cfc9d9); border-radius: 8px; font-size: 16px; }
  input:focus-visible, button:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  small { align-self: flex-end; color: var(--admin-theme-muted, #716b79); }
  p[role="alert"] { margin: 0; color: var(--admin-theme-danger, #bd0030); }
`;
const DialogActions = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); gap: 12px; margin-top: 12px;
  button { min-height: 48px; border: 1px solid var(--admin-theme-border, #e5e2eb); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-muted, #6d6774); font-size: 15px; cursor: pointer; }
  button:last-child { border-color: var(--admin-accent-deep); background: linear-gradient(135deg, var(--admin-accent), var(--admin-accent-deep)); color: #fff; font-weight: 600; }
  button:disabled { opacity: .55; cursor: default; }
  button:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;
